import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  ArrowLeft,
  Calendar,
  ListTodo,
  StickyNote,
  BookOpen,
  Bell,
  Target,
  Sparkles,
  Shield,
  ExternalLink,
  Lightbulb,
  CheckCircle2
} from "lucide-react";

// Map background URL
const MAP_BG_URL = "https://static.prod-images.emergentagent.com/jobs/e4e43130-ef55-4e75-9421-5b6729eafa95/images/c8e6201a854ebe2d95bec4089937c6c1a75c574fc5fe1d4d1880f421a76ac791.png";

const OnboardingFlow = ({ onComplete }) => {
  const [step, setStep] = useState(0);

  // All app features
  const allFeatures = [
    { emoji: "💰", name: "Spese" },
    { emoji: "🎯", name: "Abitudini" },
    { emoji: "😊", name: "Umore" },
    { emoji: "📅", name: "Calendario" },
    { emoji: "📖", name: "Diario" },
    { emoji: "📝", name: "Note" },
    { emoji: "🎡", name: "Ruota Bisogni" },
    { emoji: "✅", name: "Compiti" },
    { emoji: "🔔", name: "Promemoria" },
    { emoji: "🥗", name: "Nutrizione" }
  ];

  // Screen 1: Welcome + All Features
  const Screen1 = () => (
    <div className="text-center space-y-8 max-w-4xl mx-auto">
      {/* App Header */}
      <div>
        <div className="text-6xl mb-4">🗺️</div>
        <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
          Your Life Map
        </h1>
        <p className="text-lg text-gray-600 mt-3">
          Ideato da <strong>Laura Piccinini</strong>
        </p>
        <div className="flex justify-center gap-4 mt-2 flex-wrap">
          <a href="https://www.laurapiccinini.it" target="_blank" rel="noopener noreferrer" className="text-base text-indigo-600 hover:underline">
            www.laurapiccinini.it
          </a>
          <span className="text-gray-300 hidden sm:inline">•</span>
          <a href="https://www.psicoapplicata.org" target="_blank" rel="noopener noreferrer" className="text-base text-indigo-600 hover:underline">
            www.psicoapplicata.org
          </a>
        </div>
      </div>

      {/* Main Message */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-8 shadow-lg">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Un'app, due anime 🧠 + 🎯
        </h2>
        <p className="text-lg text-gray-700 leading-relaxed">
          <strong>Your Life Map</strong> ti permette sia di <strong>organizzare la tua quotidianità</strong>(calendario, compiti, note, promemoria) sia di <strong>migliorare abitudini e benessere</strong>(tracker, umore, nutrizione). <br />
          <span className="text-indigo-600 font-medium">Puoi usare tutto, oppure solo ciò che ti serve.</span>
        </p>
      </div>

      {/* Features Grid */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
        <p className="text-lg font-semibold text-gray-700 mb-4">🛠️ Tutte le funzionalità disponibili:</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {allFeatures.map((f) => (
            <div key={f.name} className="flex items-center gap-2 text-base text-gray-700 bg-gray-50 rounded-xl px-3 py-2 justify-center">
              <span className="text-xl">{f.emoji}</span>
              <span className="font-medium text-sm">{f.name}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Simple Rules */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { emoji: "✅", text: "Usare poco è ok" },
          { emoji: "✅", text: "Ignorare funzioni è ok" },
          { emoji: "✅", text: "Cambiare idea è ok" }
        ].map((item) => (
          <div 
            key={item.text}
            className="flex items-center justify-center gap-3 bg-green-50 border-2 border-green-200 rounded-xl px-4 py-3"
          >
            <span className="text-2xl">{item.emoji}</span>
            <span className="text-base text-gray-700 font-medium">{item.text}</span>
          </div>
        ))}
      </div>

      {/* CTA */}
      <Button 
        onClick={() => setStep(1)}
        size="lg"
        className="mt-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-10 py-7 text-xl rounded-full shadow-xl"
      >
        👉 Vedi i consigli per iniziare <ArrowRight className="ml-2 w-6 h-6" />
      </Button>

      {/* Legal Links */}
      <div className="flex justify-center gap-6 text-base pt-4">
        <Link to="/privacy" className="text-gray-500 hover:text-indigo-600 flex items-center gap-2">
          <Shield className="w-4 h-4" /> Privacy Policy
        </Link>
        <Link to="/termini" className="text-gray-500 hover:text-indigo-600 flex items-center gap-2">
          <ExternalLink className="w-4 h-4" /> Termini di Servizio
        </Link>
      </div>
      
      <p className="text-sm text-gray-400">
        Your Life Map © 2025 - Tutti i diritti riservati
      </p>
    </div>
  );

  // Screen 2: Tips for both organizing and improving
  const Screen2 = () => (
    <div className="text-center space-y-8 max-w-5xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
          Your Life Map
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Laura Piccinini • <a href="https://www.laurapiccinini.it" className="text-indigo-600 hover:underline">laurapiccinini.it</a> • <a href="https://www.psicoapplicata.org" className="text-indigo-600 hover:underline">psicoapplicata.org</a>
        </p>
      </div>

      <h2 className="text-3xl font-bold text-gray-900">
        💡 Consigli per iniziare al meglio
      </h2>

      {/* Two columns: Organization + Improvement */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Column 1: Organization */}
        <Card className="bg-white/95 backdrop-blur-sm shadow-lg border-t-4 border-t-blue-500">
          <CardContent className="p-6 text-left">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Per organizzarti</h3>
                <p className="text-sm text-gray-500">Calendario, compiti, note, promemoria</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-xl p-4">
                <p className="font-semibold text-gray-800 mb-2">📋 Compiti di Vita</p>
                <p className="text-sm text-gray-600">
                  Organizza i tuoi compiti per <strong>area</strong> (Casa, Lavoro, Studio, Salute...). 
                  Puoi trascinarli, riordinarli e spostarli tra categorie.
                </p>
              </div>
              
              <div className="bg-blue-50 rounded-xl p-4">
                <p className="font-semibold text-gray-800 mb-2">📅 Calendario & Promemoria</p>
                <p className="text-sm text-gray-600">
                  Aggiungi appuntamenti e attiva i <strong>promemoria con allarme</strong> 
                  per non dimenticare nulla di importante.
                </p>
              </div>
              
              <div className="bg-blue-50 rounded-xl p-4">
                <p className="font-semibold text-gray-800 mb-2">📝 Note & Diario</p>
                <p className="text-sm text-gray-600">
                  Usa le <strong>Note</strong> per appunti veloci e il <strong>Diario</strong> 
                  per riflessioni più profonde.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Column 2: Improvement */}
        <Card className="bg-white/95 backdrop-blur-sm shadow-lg border-t-4 border-t-purple-500">
          <CardContent className="p-6 text-left">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Target className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Per migliorare</h3>
                <p className="text-sm text-gray-500">Abitudini, umore, nutrizione, benessere</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-purple-50 rounded-xl p-4">
                <p className="font-semibold text-gray-800 mb-2">🎯 La regola d'oro</p>
                <p className="text-sm text-gray-600">
                  <strong>1 area di focus</strong> → <strong>1 obiettivo</strong> → <strong>max 2 micro-abitudini</strong>. 
                  Meno obiettivi = più probabilità di riuscirci.
                </p>
              </div>
              
              <div className="bg-purple-50 rounded-xl p-4">
                <p className="font-semibold text-gray-800 mb-2">📊 Non devi tracciare tutto ogni giorno</p>
                <p className="text-sm text-gray-600">
                  Scegli <strong>1–2 aree</strong> e traccia solo quelle. 
                  Anche <strong>2–3 volte a settimana</strong> basta per capire i tuoi schemi.
                </p>
              </div>
              
              <div className="bg-purple-50 rounded-xl p-4">
                <p className="font-semibold text-gray-800 mb-2">⏱️ Dai tempo al cambiamento</p>
                <p className="text-sm text-gray-600">
                  Per <strong>4–8 settimane</strong> lavora su <strong>una cosa sola</strong>. 
                  Il resto lo metti in "In arrivo".
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Example areas */}
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
        <p className="font-semibold text-gray-700 mb-4">💡 Esempi di aree su cui lavorare:</p>
        <div className="flex flex-wrap justify-center gap-3">
          {[
            { emoji: "🧘", text: "Stress / Calma" },
            { emoji: "😴", text: "Sonno / Energia" },
            { emoji: "🏃", text: "Movimento" },
            { emoji: "🥗", text: "Alimentazione" },
            { emoji: "💰", text: "Finanze" },
            { emoji: "📚", text: "Studio" },
            { emoji: "❤️", text: "Relazioni" }
          ].map((area) => (
            <Badge key={area.text} variant="outline" className="text-base px-4 py-2 bg-gray-50">
              {area.emoji} {area.text}
            </Badge>
          ))}
        </div>
      </div>

      {/* Final reminder */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 shadow-lg max-w-2xl mx-auto">
        <Sparkles className="w-8 h-8 text-indigo-500 mx-auto mb-3" />
        <p className="text-lg text-gray-700">
          <strong>Ricorda:</strong> non esiste il modo "giusto" di usare questa app. 
          Esiste solo quello che <strong>ti aiuta davvero</strong>.
        </p>
        <p className="text-sm text-gray-500 mt-2">
          Puoi sempre cambiare approccio dalle Impostazioni.
        </p>
      </div>

      {/* Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
        <Button 
          variant="outline"
          onClick={() => setStep(0)}
          className="rounded-full px-8 py-6 text-lg"
        >
          <ArrowLeft className="w-5 h-5 mr-2" /> Indietro
        </Button>
        <Button 
          onClick={onComplete}
          size="lg"
          className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-12 py-7 text-xl rounded-full shadow-xl"
        >
          🚀 Inizia ad usare l'app <ArrowRight className="ml-2 w-6 h-6" />
        </Button>
      </div>

      {/* Footer */}
      <div className="pt-6 border-t border-gray-200">
        <p className="text-sm text-gray-500">
          Your Life Map © 2025 • Laura Piccinini • 
          <Link to="/privacy" className="text-indigo-600 hover:underline ml-2">Privacy</Link> • 
          <Link to="/termini" className="text-indigo-600 hover:underline ml-2">Termini</Link>
        </p>
      </div>
    </div>
  );

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{
        backgroundImage: `url(${MAP_BG_URL})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-white/85" />
      
      {/* Progress bar */}
      <div className="relative z-10 w-full h-2 bg-gray-200">
        <div 
          className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500"
          style={{ width: step === 0 ? '50%' : '100%' }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex items-center justify-center p-6 sm:p-8 overflow-auto">
        {step === 0 ? <Screen1 /> : <Screen2 />}
      </div>
    </div>
  );
};

export default OnboardingFlow;
